import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const ShareMyFlights = () => {
  // State
  const [settings, setSettings] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    relationship: '',
    notification_level: 'essentials'
  });

  // Load settings and contacts on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Get settings
      const settingsRes = await fetch('/api/v1/sharing/settings', {
        credentials: 'include'
      });
      const settingsData = await settingsRes.json();
      setSettings(settingsData);

      // Get contacts
      const contactsRes = await fetch('/api/v1/sharing/contacts', {
        credentials: 'include'
      });
      const contactsData = await contactsRes.json();
      setContacts(contactsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMasterSwitch = async () => {
    try {
      const newValue = !settings?.sharing_enabled;
      const res = await fetch('/api/v1/sharing/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sharing_enabled: newValue })
      });
      const updated = await res.json();
      setSettings(updated);
    } catch (error) {
      console.error('Failed to toggle sharing:', error);
    }
  };

  const handleUpdateSettings = async (field, value) => {
    try {
      const res = await fetch('/api/v1/sharing/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });
      const updated = await res.json();
      setSettings(updated);
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const handleAddContact = async () => {
    try {
      const res = await fetch('/api/v1/sharing/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      const newContact = await res.json();
      setContacts([...contacts, newContact]);
      setShowAddDialog(false);
      resetForm();
    } catch (error) {
      console.error('Failed to add contact:', error);
    }
  };

  const handleUpdateContact = async (contactId) => {
    try {
      const res = await fetch(`/api/v1/sharing/contacts/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      const updated = await res.json();
      setContacts(contacts.map(c => c.id === contactId ? updated : c));
      setEditingContact(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update contact:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!confirm('Remove this contact? They will no longer receive notifications.')) return;
    
    try {
      await fetch(`/api/v1/sharing/contacts/${contactId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setContacts(contacts.filter(c => c.id !== contactId));
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  const openEditDialog = (contact) => {
    setFormData({
      contact_name: contact.contact_name,
      contact_email: contact.contact_email,
      contact_phone: contact.contact_phone || '',
      relationship: contact.relationship || '',
      notification_level: contact.notification_level
    });
    setEditingContact(contact);
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      relationship: '',
      notification_level: 'essentials'
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon fontSize="large" />
          Share My Flights
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Keep your family in the loop. Set it and forget it.
        </Typography>
      </Box>

      {/* Master Toggle */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Enable Flight Sharing</Typography>
              <Typography variant="body2" color="text.secondary">
                Allow contacts to receive notifications about your flights
              </Typography>
            </Box>
            <Switch
              checked={settings?.sharing_enabled || false}
              onChange={handleToggleMasterSwitch}
              size="large"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Contacts Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">My Contacts ({contacts.length}/2)</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
              disabled={contacts.length >= 2}
            >
              Add Contact
            </Button>
          </Box>

          {contacts.length === 0 ? (
            <Alert severity="info">
              No contacts added yet. Add up to 2 family members or friends to share your flights.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {contacts.map((contact) => (
                <Card key={contact.id} variant="outlined">
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PersonIcon color="primary" />
                          <Typography variant="h6">{contact.contact_name}</Typography>
                          <Chip 
                            label={contact.relationship || 'Contact'} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">{contact.contact_email}</Typography>
                          </Box>
                          {contact.contact_phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="body2">{contact.contact_phone}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Notification Level</InputLabel>
                          <Select
                            value={contact.notification_level}
                            label="Notification Level"
                            onChange={(e) => {
                              const updatedContact = { ...contact, notification_level: e.target.value };
                              handleUpdateContact(updatedContact.id);
                            }}
                          >
                            <MenuItem value="minimal">Minimal</MenuItem>
                            <MenuItem value="essentials">Essentials</MenuItem>
                            <MenuItem value="detailed">Detailed</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton onClick={() => openEditDialog(contact)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteContact(contact.id)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Notification Settings</Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>What to Share</Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Takeoff notifications</Typography>
                  <Switch
                    size="small"
                    checked={settings?.share_takeoff || false}
                    onChange={(e) => handleUpdateSettings('share_takeoff', e.target.checked)}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Landing notifications</Typography>
                  <Switch
                    size="small"
                    checked={settings?.share_landing || false}
                    onChange={(e) => handleUpdateSettings('share_landing', e.target.checked)}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Delay notifications (&gt;30 min)</Typography>
                  <Switch
                    size="small"
                    checked={settings?.share_delays || false}
                    onChange={(e) => handleUpdateSettings('share_delays', e.target.checked)}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Hotel arrival estimates</Typography>
                  <Switch
                    size="small"
                    checked={settings?.share_hotel_info || false}
                    onChange={(e) => handleUpdateSettings('share_hotel_info', e.target.checked)}
                  />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Message Style</InputLabel>
                <Select
                  value={settings?.message_style || 'friendly'}
                  label="Message Style"
                  onChange={(e) => handleUpdateSettings('message_style', e.target.value)}
                >
                  <MenuItem value="minimal">Minimal (Just the facts)</MenuItem>
                  <MenuItem value="friendly">Friendly (Recommended)</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Max Notifications Per Day</InputLabel>
                <Select
                  value={settings?.max_notifications_per_day || 10}
                  label="Max Notifications Per Day"
                  onChange={(e) => handleUpdateSettings('max_notifications_per_day', e.target.value)}
                >
                  <MenuItem value={5}>5 per day</MenuItem>
                  <MenuItem value={10}>10 per day (Recommended)</MenuItem>
                  <MenuItem value={20}>20 per day</MenuItem>
                  <MenuItem value={999}>Unlimited</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="caption" color="text.secondary">
                Quiet hours: 10pm - 8am (notifications paused)
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={showAddDialog} onClose={() => { setShowAddDialog(false); setEditingContact(null); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone (Optional)"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Relationship"
              value={formData.relationship}
              onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
              placeholder="Spouse, Parent, Friend, etc."
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Notification Level</InputLabel>
              <Select
                value={formData.notification_level}
                label="Notification Level"
                onChange={(e) => setFormData({ ...formData, notification_level: e.target.value })}
              >
                <MenuItem value="minimal">Minimal - Just takeoff/landing</MenuItem>
                <MenuItem value="essentials">Essentials - Key updates (Recommended)</MenuItem>
                <MenuItem value="detailed">Detailed - Everything</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowAddDialog(false); setEditingContact(null); resetForm(); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => editingContact ? handleUpdateContact(editingContact.id) : handleAddContact()}
            disabled={!formData.contact_name || !formData.contact_email}
          >
            {editingContact ? 'Update' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareMyFlights;
